// AI Workforce Constants
// Extracted from AIWorkforcePage.js for better organization

export const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  pending_activation: 'bg-amber-100 text-amber-700',
  active: 'bg-emerald-100 text-emerald-700',
  suspended: 'bg-red-100 text-red-700',
  deactivated: 'bg-slate-200 text-slate-500'
};

export const TASK_STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  awaiting_info: 'bg-purple-100 text-purple-700',
  awaiting_approval: 'bg-orange-100 text-orange-700',
  completed: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-100 text-slate-700'
};

export const AGENT_TYPES = [
  { value: 'content_writer', label: 'Content Writer', icon: 'FileText' },
  { value: 'designer', label: 'Designer', icon: 'Palette' },
  { value: 'analyst', label: 'Analyst', icon: 'BarChart3' },
  { value: 'researcher', label: 'Researcher', icon: 'Search' },
  { value: 'strategist', label: 'Strategist', icon: 'Target' },
  { value: 'leader', label: 'Leader/Manager', icon: 'Users' }
];

export const DELIVERABLE_TYPES = [
  { value: 'document', label: 'Document' },
  { value: 'image', label: 'Image' },
  { value: 'analysis', label: 'Analysis' },
  { value: 'email', label: 'Email' },
  { value: 'task_output', label: 'Task Output' }
];

export const SUPPORT_LEVELS = [
  { value: 'basic', label: 'Basic' },
  { value: 'standard', label: 'Standard' },
  { value: 'premium', label: 'Premium' },
  { value: 'enterprise', label: 'Enterprise' }
];

export const PRIORITY_OPTIONS = [
  { value: 1, label: '1 - Critical' },
  { value: 2, label: '2 - High' },
  { value: 3, label: '3 - High' },
  { value: 5, label: '5 - Normal' },
  { value: 7, label: '7 - Low' },
  { value: 10, label: '10 - Lowest' }
];

export const RESOURCE_CATEGORIES = [
  { value: 'product', label: 'Product' },
  { value: 'logo', label: 'Logo' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'background', label: 'Background' },
  { value: 'other', label: 'Other' }
];

export const OVERNIGHT_TASK_TYPES = [
  { value: 'ai_execution', label: 'AI Execution (Demo Mode)' },
  { value: 'content_generation', label: 'Content Generation' },
  { value: 'data_analysis', label: 'Data Analysis' },
  { value: 'lead_research', label: 'Lead Research' },
  { value: 'market_research', label: 'Market Research' },
  { value: 'report_generation', label: 'Report Generation' }
];

export const DEFAULT_OVERNIGHT_TASK_FORM = {
  task_type: 'ai_execution',
  title: '',
  description: '',
  custom_input: '',
  priority: 5
};

export const SCHEDULE_TYPES = [
  { value: 'manual', label: 'Manual (Queue on Demand)' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

export const PROCESS_TASK_TYPES = [
  { value: 'ai_execution', label: 'AI Execution' },
  { value: 'content_generation', label: 'Content Generation' },
  { value: 'data_analysis', label: 'Data Analysis' },
  { value: 'lead_research', label: 'Lead Research' },
  { value: 'market_research', label: 'Market Research' },
  { value: 'report_generation', label: 'Report Generation' },
  { value: 'video_generation', label: 'Video Generation' }
];

export const PROCESS_PRIORITY_OPTIONS = [
  { value: 1, label: '1 - Critical' },
  { value: 3, label: '3 - High' },
  { value: 5, label: '5 - Normal' },
  { value: 7, label: '7 - Low' }
];

export const DEFAULT_PROCESS_FORM = {
  name: '',
  description: '',
  tasks: [],
  schedule_type: 'manual'
};

export const DEFAULT_PROCESS_TASK_FORM = {
  task_type: 'ai_execution',
  title: '',
  description: '',
  custom_input: '',
  priority: 5
};

// Knowledge Base constants
export const KNOWLEDGE_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'product', label: 'Product' },
  { value: 'sales', label: 'Sales' },
  { value: 'support', label: 'Support' },
  { value: 'policy', label: 'Policy' },
  { value: 'training', label: 'Training' }
];

export const DEFAULT_KNOWLEDGE_FORM = {
  title: '',
  category: 'general',
  content: '',
  entries: [{ title: '', content: '' }],
  source_type: 'manual',
  assigned_agents: []
};

// Guidelines constants
export const GUIDELINE_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'communication', label: 'Communication' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'quality', label: 'Quality' },
  { value: 'security', label: 'Security' },
  { value: 'brand', label: 'Brand' }
];

export const ENFORCEMENT_LEVELS = [
  { value: 'strict', label: 'Strict', color: 'red', description: 'AI must always follow this rule. Violations will block task completion.' },
  { value: 'warning', label: 'Warning', color: 'amber', description: 'AI should follow this rule. Violations will trigger a warning but allow completion.' },
  { value: 'suggestion', label: 'Suggestion', color: 'blue', description: 'AI is encouraged to follow this rule. Provides guidance without enforcement.' }
];

export const DEFAULT_GUIDELINE_FORM = {
  title: '',
  category: 'general',
  enforcement_level: 'suggestion',
  description: '',
  rules: [{ rule: '', explanation: '' }],
  assigned_agents: []
};

// SOP constants
export const SOP_TRIGGER_TYPES = [
  { value: 'manual', label: 'Manual' },
  { value: 'event', label: 'Event-Based' },
  { value: 'schedule', label: 'Scheduled' },
  { value: 'automatic', label: 'Automatic' }
];

export const SOP_ACTION_TYPES = [
  { value: 'process', label: 'Process' },
  { value: 'decision', label: 'Decision' },
  { value: 'action', label: 'Action' },
  { value: 'wait', label: 'Wait' },
  { value: 'notify', label: 'Notify' }
];

export const DEFAULT_SOP_FORM = {
  name: '',
  description: '',
  trigger_type: 'manual',
  status: 'draft',
  steps: [{ title: '', description: '', action_type: 'process' }],
  assigned_agents: []
};

// Model options for AI configuration
export const MODEL_OPTIONS = {
  openai: [
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' }
  ],
  anthropic: [
    { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
    { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
    { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' }
  ],
  gemini: [
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' }
  ]
};

export const AI_PROVIDERS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'gemini', label: 'Google Gemini' }
];

export const DEFAULT_AI_CONFIG = {
  left_brain_provider: 'openai',
  left_brain_model: 'gpt-4o',
  right_brain_provider: 'anthropic',
  right_brain_model: 'claude-sonnet-4-5-20250929',
  brain_stem_provider: 'gemini',
  brain_stem_model: 'gemini-2.5-flash',
  temperature: 0.7,
  max_tokens: 2000
};
